<script setup lang="ts">
import { useGlobalCriterias, useSiteCriterias } from "@/api/vibration-criteria-query";
import { useSiteWeeklyLimits } from "@/api/weekly-limit-query";
import { Alert } from "@soft-db/monitoring-api-client";
import { Badge, Card, CardTitle } from "@soft-db/ui";
import { useConditionTypeLabel } from "../composable/condition-label";
import AlertMappingLimitDisplay from "./AlertMappingLimitDisplay.vue";
import AlertMappingRow from "./AlertMappingRow.vue";
import { ConditionMapping } from "./types";

const { t } = useI18n();

const props = defineProps<{
	fromSite: string;
	toSite: string;
	alert: Alert;
	conditions: ConditionMapping[];
}>();

const { weeklyLimits: fromWL } = useSiteWeeklyLimits(() => props.fromSite);
const { vibrationCriterias: fromVC } = useSiteCriterias(() => props.fromSite);

const { weeklyLimits: toWL } = useSiteWeeklyLimits(() => props.toSite);
const { vibrationCriterias: toVC } = useSiteCriterias(() => props.toSite);

const { globalVibrationCriterias } = useGlobalCriterias();

function resolveLimitName(type: ConditionMapping["type"], id: string, source: "from" | "to") {
	if (type === "vibration_event") {
		const criterias = [...(source === "from" ? fromVC.value : toVC.value)];
		criterias.push(...globalVibrationCriterias.value);

		return criterias.find((vc) => vc.id === id)?.name || "";
	}

	if (type === "weekly_limit") {
		const limits = source === "from" ? fromWL.value : toWL.value;

		return limits?.find((wl) => wl.id === id)?.name || "";
	}

	return "";
}

const limitKeys = [
	{ severity: "error", from: "originalError", to: "newError" },
	{ severity: "warning", from: "originalWarning", to: "newWarning" },
] as const;

const conditionTypeLabel = useConditionTypeLabel();
</script>

<template>
	<Card>
		<CardTitle class="flex items-center justify-between p-3">
			<div>{{ alert.name }}</div>
			<Badge color="primary" variant="soft">{{ conditionTypeLabel(alert.type) }}</Badge>
		</CardTitle>

		<template v-if="conditions.length > 0">
			<div class="border-border border-t">
				<div v-for="condition of conditions" :key="condition.index" class="flex flex-col gap-2 p-3">
					<template v-for="limit of limitKeys" :key="limit.severity">
						<AlertMappingRow v-if="condition[limit.from]">
							<template #left>
								<AlertMappingLimitDisplay
									:name="resolveLimitName(condition.type, condition[limit.from]!, 'from')"
									:severity="limit.severity"
								/>
							</template>
							<template #right>
								<AlertMappingLimitDisplay
									v-if="condition[limit.to]"
									:name="resolveLimitName(condition.type, condition[limit.to]!, 'to')"
									:severity="limit.severity"
								/>
								<div v-else class="opacity-70">{{ t("common.none") }}</div>
							</template>
						</AlertMappingRow>
					</template>
				</div>
			</div>
		</template>
	</Card>
</template>
